import { createContext, useCallback } from 'react';

import { toastManager } from './toastManager';
import type { Toast, ToasterContextType, ToasterProviderProps } from './types';

export const ToasterContext = createContext<ToasterContextType | null>(null);

toastManager.configure({ maxToasts: 1, defaultDuration: 5000 });

/**
 * ToasterProvider provides the toast context for useToast hook.
 * Note: This only provides the context - the actual toast UI is rendered by ToastOverlay.
 * Place ToastOverlay at the root level of your app, outside of navigation.
 */
const Toaster = ({ children }: ToasterProviderProps) => {
  const dismiss = useCallback((id: string) => {
    toastManager.dismiss(id);
  }, []);

  const toast = useCallback((options: Omit<Toast, 'id'>): string => {
    return toastManager.toast(options);
  }, []);

  const dismissAll = useCallback(() => {
    toastManager.dismissAll();
  }, []);

  const contextValue: ToasterContextType = {
    toast,
    dismiss,
    dismissAll,
  };

  return (
    <ToasterContext.Provider value={contextValue}>
      {children}
    </ToasterContext.Provider>
  );
};

Toaster.displayName = 'Toaster';

export { Toaster };
